// Two true stories Lea has carried home from the salon this season — added
// at Glenn's request directly under Lea's intro because they say more about
// what dementia actually does than any data slide could. The "George" story
// is also the most direct emotional bridge into why a Pet Edition belongs
// in remembr's launch trio: the dog made it through where the daughter
// could no longer be recognised.
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PawPrint, UserCircle } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";

const FADE = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.95, ease: [0.16, 1, 0.3, 1] },
};

const STORIES = [
    {
        key: "george",
        Icon: PawPrint,
        accent: "#C97B4A",
        bg: "linear-gradient(160deg, #FBEFE3 0%, #FFFFFF 60%, #FBEFE3 100%)",
        border: "#E6B999",
    },
    {
        key: "mirror",
        Icon: UserCircle,
        accent: "#6B7DB8",
        bg: "linear-gradient(160deg, #F4F6FB 0%, #FFFFFF 60%, #F4F6FB 100%)",
        border: "#8C9ECB",
    },
];

export function LeaSalonStories() {
    const { t } = useTranslation();
    return (
        <section
            className="relative pt-2 pb-32 px-6 rem-petal-bg overflow-hidden"
            data-testid="lea-salon-stories"
            id="from-the-salon"
        >
            <div className="max-w-6xl mx-auto">
                <motion.div {...FADE} className="text-center max-w-3xl mx-auto mb-12">
                    <div className="flex justify-center mb-5">
                        <ForgetMeNotIcon size={40} />
                    </div>
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5" data-testid="salon-stories-eyebrow">
                        {t("lea_salon.eyebrow", "From the salon, this season")}
                    </p>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2D2B2A] leading-[1.1] tracking-tight" data-testid="salon-stories-headline">
                        {t("lea_salon.headline_part1", "Two true things Lea carried home")} {" "}
                        <em className="italic text-[#4A5A95]">{t("lea_salon.headline_emphasis", "this week.")}</em>
                    </h2>
                    <p className="mt-6 text-base sm:text-lg text-[#5C5A59] leading-relaxed font-light max-w-2xl mx-auto">
                        {t(
                            "lea_salon.lede",
                            "Names withheld. Permission given. The salon chair sees what very few rooms see — what dementia actually does to a person, and what unexpectedly still gets through.",
                        )}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
                    {STORIES.map((s, i) => {
                        const Icon = s.Icon;
                        return (
                            <motion.article
                                key={s.key}
                                {...FADE}
                                transition={{ ...FADE.transition, delay: 0.1 + i * 0.12 }}
                                className="relative rounded-[2rem] p-8 sm:p-10 border shadow-rem-soft hover:shadow-rem-card transition-shadow"
                                style={{ background: s.bg, borderColor: `${s.border}55` }}
                                data-testid={`salon-story-${s.key}`}
                            >
                                <div className="flex items-center gap-3 mb-5">
                                    <span
                                        className="inline-flex w-11 h-11 rounded-2xl items-center justify-center shrink-0"
                                        style={{ background: `${s.accent}22` }}
                                    >
                                        <Icon size={22} weight="fill" color={s.accent} />
                                    </span>
                                    <p
                                        className="font-sans text-[10px] uppercase tracking-[0.28em] font-semibold"
                                        style={{ color: s.accent }}
                                    >
                                        {t(`lea_salon.${s.key}.kicker`)}
                                    </p>
                                </div>
                                <h3 className="font-serif text-2xl sm:text-3xl text-[#2D2B2A] leading-tight" data-testid={`salon-story-${s.key}-title`}>
                                    {t(`lea_salon.${s.key}.title`)}
                                </h3>
                                <div className="mt-5 space-y-4 text-base text-[#5C5A59] leading-relaxed font-light">
                                    <p data-testid={`salon-story-${s.key}-body`}>
                                        {t(`lea_salon.${s.key}.body`)}
                                    </p>
                                    <p
                                        className="font-serif italic text-lg leading-snug text-balance"
                                        style={{ color: s.accent }}
                                        data-testid={`salon-story-${s.key}-pullquote`}
                                    >
                                        {t(`lea_salon.${s.key}.pullquote`)}
                                    </p>
                                    <p className="text-sm text-[#8A8786]" data-testid={`salon-story-${s.key}-reflection`}>
                                        {t(`lea_salon.${s.key}.reflection`)}
                                    </p>
                                </div>
                            </motion.article>
                        );
                    })}
                </div>

                <motion.div {...FADE} transition={{ ...FADE.transition, delay: 0.4 }} className="mt-12 max-w-3xl mx-auto text-center">
                    <p className="font-serif italic text-lg sm:text-xl text-[#4A5A95] leading-relaxed text-balance" data-testid="salon-stories-bridge">
                        {t(
                            "lea_salon.bridge",
                            "These are not edge cases. They are the rule that remembr was built to meet — and a quiet part of why the Pet Edition belongs in our launch.",
                        )}
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
